import React, { useState, useEffect } from 'react';
import { Wrench, ScrollText, BookOpen } from 'lucide-react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { ToolUseContent } from './ToolUseContent';
import { AgentActivityPill } from './AgentActivityPill';
import { getToolUseDisplay } from '../../utils/toolUse';
import { getSourceIcon } from '../../utils/sourceIcons';

import { useTranslation } from 'react-i18next';

const ACTIVITY_PILL_TOOLS = new Set(['loadTools', 'loadSkill', 'getSkillResource']);

interface ToolUseGroupProps {
  tools: ToolUseBlock[];
  onExpand?: () => void;
  preventToolUseBlockToCollapse?: boolean;
}

/**
 * Icon for the collapsed pill — uses the source icon of the given tool, or a
 * generic play icon for tools without a known source.
 */
/** Identical sizing/treatment to the icon in ToolUseContent so the row height is the same when collapsed vs expanded. */
function CollapsedIcon({ tool }: { tool: ToolUseBlock }) {
  const source = (tool.meta as { source?: string })?.source;
  const icon = source ? getSourceIcon(source) : null;

  if (icon) {
    return (
      <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-800">
        {icon}
      </div>
    );
  }

  const ActivityIcon =
    tool.name === 'loadSkill'
      ? ScrollText
      : tool.name === 'loadTools'
        ? Wrench
        : tool.name === 'getSkillResource'
          ? BookOpen
          : null;

  return (
    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-800">
      {ActivityIcon ? (
        <ActivityIcon className="h-3.5 w-3.5 text-[#FF732E]" strokeWidth={2} />
      ) : (
        <Wrench className="h-3.5 w-3.5 text-[#FF732E]" strokeWidth={2} />
      )}
    </div>
  );
}

/**
 * Renders 2+ consecutive generic tool-use blocks as a collapsible stepper
 * timeline. Each pair of items is connected by a crisp gradient line aligned
 * to the icon column.
 *
 * States:
 *   Pending  → always expanded, collapse affordance hidden
 *   Done     → auto-collapses; collapsed pill shows first tool's icon + label
 */
export const ToolUseGroup: React.FC<ToolUseGroupProps> = ({
  tools,
  onExpand,
  preventToolUseBlockToCollapse,
}) => {
  const { t } = useTranslation('pleri/chat');
  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );

  const anyPending = useChatStore((state) => {
    if (!activeConversationId) {
      return false;
    }
    const conv = state.conversations[activeConversationId];
    if (!conv?.isLoading) {
      return false;
    }
    const pending = conv.pendingToolUses ?? new Set<string>();
    return tools.some((t) => pending.has(t.id));
  });

  const [collapsed, setCollapsed] = useState(!anyPending);
  useEffect(() => {
    if (!anyPending) {
      setCollapsed(true);
    }
  }, [anyPending]);

  const count = tools.length;
  const overflow = count - 1;

  // ── Collapsed pill ──────────────────────────────────────────────────────────
  if (collapsed) {
    const firstName = getToolUseDisplay(tools[0]);
    const label =
      overflow > 0 ? t('toolAndMore', { firstName, overflow }) : firstName;

    return (
      <div className="my-0.5 select-none">
        <button
          onClick={() => setCollapsed(false)}
          className="group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800/40"
        >
          <CollapsedIcon tool={tools[0]} />

          <span className="min-w-0 flex-1 truncate text-sm font-normal tracking-wide text-gray-500 dark:text-gray-400">
            {label}
          </span>

          {/* Chevron — only on hover */}
          <svg
            className="h-3.5 w-3.5 flex-shrink-0 text-transparent transition-colors duration-150 group-hover:text-gray-400 dark:group-hover:text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 8.25l-7.5 7.5-7.5-7.5"
            />
          </svg>
        </button>
      </div>
    );
  }

  // ── Expanded stepper ────────────────────────────────────────────────────────
  //
  // Connector geometry:
  //   ToolUseContent has px-2.5 (10px) left padding, then a h-5 w-5 icon.
  //   Icon center-x from item left edge = 10 + 10 = 20px.
  //   w-px connector centered at 19.5 → marginLeft: 19.
  //
  // The gradient runs from a solid-feeling top edge to transparent, so the
  // line looks crisp where it leaves the icon and fades into the next row.
  return (
    <div className="my-0.5 select-none">
      {tools.map((tool, i) => (
        <React.Fragment key={tool.id}>
          {i > 0 && (
            <div
              className="w-px bg-gradient-to-b from-gray-400/80 via-gray-400/40 to-transparent dark:from-gray-500/70 dark:via-gray-500/25 dark:to-transparent"
              style={{ height: 16, marginLeft: 19 }}
            />
          )}
          {ACTIVITY_PILL_TOOLS.has(tool.name) ? (
            <AgentActivityPill content={tool} outerClassName="my-0" />
          ) : (
            <ToolUseContent
              content={tool}
              onExpand={onExpand}
              preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              outerClassName="my-0"
            />
          )}
        </React.Fragment>
      ))}
      {/* Collapse button — chevron only, no text, hidden while pending */}
      {!anyPending && (
        <button
          onClick={() => setCollapsed(true)}
          className="group mt-1 flex items-center rounded px-[11px] py-0.5 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800/40"
          aria-label={t('collapseActions')}
        >
          <svg
            className="h-3 w-3 text-gray-300/80 transition-colors duration-150 group-hover:text-gray-400 dark:text-gray-600/60 dark:group-hover:text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.5 15.75l7.5-7.5 7.5 7.5"
            />
          </svg>
        </button>
      )}
    </div>
  );
};
